import {Color} from '@benchling/nucleus-ui';
import {Button as MantineButton} from '@mantine/core';
import {DateInput, DatePicker, DatesRangeValue} from '@mantine/dates';
import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React, {useEffect, useState} from 'react';

import {FONT_PROXIMA_14} from 'styles/FontConstants';
import {USER_EDIT_DATE_FORMAT} from 'util/DateConstants';

export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

export type FilterMode = 'between' | 'before' | 'after';

export const FILTER_MODE = {
  BETWEEN: 'between',
  BEFORE: 'before',
  AFTER: 'after',
} as const satisfies Record<string, FilterMode>;

export interface FlexibleDateRangePickerProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  mode: FilterMode;
  onModeChange: (mode: FilterMode) => void;
}

const DISPLAY_DATE_FORMAT = 'M/D/YYYY';

function formatToISODate(date: Date | null): string | null {
  if (!date) {
    return null;
  }
  return moment(date).format(USER_EDIT_DATE_FORMAT);
}

function parseISODate(dateString: string | null): Date | null {
  if (!dateString) {
    return null;
  }
  const parsed = moment(dateString, USER_EDIT_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
}

// Only accept dates with 4-digit years (>= 1000) to prevent the calendar from jumping around while typing
function isValidYear(date: Date | null): boolean {
  return date != null && date.getFullYear() >= 1000;
}

const MODE_OPTIONS: Array<{value: FilterMode; label: string}> = [
  {value: FILTER_MODE.BETWEEN, label: 'Between'},
  {value: FILTER_MODE.BEFORE, label: 'Before'},
  {value: FILTER_MODE.AFTER, label: 'After'},
];

export default function FlexibleDateRangePicker({
  value,
  onChange,
  mode,
  onModeChange,
}: FlexibleDateRangePickerProps): JSX.Element {
  // Used to control the date that is displayed in the calendar which is needed for typing into the inputs
  const [displayedDate, setDisplayedDate] = useState<Date>(
    parseISODate(value.startDate) ?? parseISODate(value.endDate) ?? new Date()
  );

  // Local state for input values - allows typing incomplete dates without affecting parent/calendar
  const [inputStartDate, setInputStartDate] = useState<Date | null>(parseISODate(value.startDate));
  const [inputEndDate, setInputEndDate] = useState<Date | null>(parseISODate(value.endDate));

  // Compute validation error for date order in "between" mode
  const dateOrderError = React.useMemo(() => {
    if (
      mode === FILTER_MODE.BETWEEN &&
      inputStartDate &&
      inputEndDate &&
      isValidYear(inputStartDate) &&
      isValidYear(inputEndDate) &&
      inputStartDate > inputEndDate
    ) {
      return 'Start date must be before end date';
    }
    return undefined;
  }, [mode, inputStartDate, inputEndDate]);

  // Sync local input state when parent value changes (e.g., from calendar clicks or mode changes)
  useEffect(() => {
    setInputStartDate(parseISODate(value.startDate));
    setInputEndDate(parseISODate(value.endDate));
  }, [value.startDate, value.endDate]);

  const handleModeChange = (newMode: FilterMode): void => {
    if (mode === newMode) {
      return;
    }
    onModeChange(newMode);

    if (newMode === FILTER_MODE.AFTER) {
      // Use previous endDate if any, clear startDate
      onChange({
        startDate: value.endDate,
        endDate: null,
      });
      const dateToDisplay = (value.endDate && parseISODate(value.endDate)) || new Date();
      setDisplayedDate(dateToDisplay);
    } else if (newMode === FILTER_MODE.BEFORE) {
      // Use previous startDate if any, clear endDate
      onChange({
        startDate: null,
        endDate: value.startDate,
      });
      const dateToDisplay = (value.startDate && parseISODate(value.startDate)) || new Date();
      setDisplayedDate(dateToDisplay);
    } else if (newMode === FILTER_MODE.BETWEEN) {
      // Clear both dates, show current month
      onChange({
        startDate: null,
        endDate: null,
      });
      setDisplayedDate(new Date());
    }
  };

  // Handler for calendar clicks which updates parent value directly
  const handleSingleDateChange = (date: Date | null): void => {
    const formattedDate = formatToISODate(date);
    if (mode === FILTER_MODE.AFTER) {
      onChange({startDate: formattedDate, endDate: null});
    } else if (mode === FILTER_MODE.BEFORE) {
      onChange({startDate: null, endDate: formattedDate});
    }
  };

  // Handler for calendar clicks which updates parent value directly
  const handleRangeChange = (range: DatesRangeValue): void => {
    const [startDate, endDate] = range;
    onChange({
      startDate: formatToISODate(startDate),
      endDate: formatToISODate(endDate),
    });
  };

  // Handler for DateInput changes which updates local state always, parent only when valid date
  const handleStartInputChange = (date: Date | null): void => {
    setInputStartDate(date);
    // Only update parent and navigate calendar if year is valid (4 digits) or clearing
    if (date == null || isValidYear(date)) {
      onChange({
        startDate: formatToISODate(date),
        endDate: value.endDate,
      });
      if (date) {
        setDisplayedDate(date);
      }
    }
  };

  const handleEndInputChange = (date: Date | null): void => {
    setInputEndDate(date);
    if (date == null || isValidYear(date)) {
      onChange({
        startDate: value.startDate,
        endDate: formatToISODate(date),
      });
      if (date) {
        setDisplayedDate(date);
      }
    }
  };

  const handleSingleInputChange = (date: Date | null): void => {
    if (mode === FILTER_MODE.AFTER) {
      setInputStartDate(date);
    } else {
      setInputEndDate(date);
    }
    if (date == null || isValidYear(date)) {
      handleSingleDateChange(date);
      if (date) {
        setDisplayedDate(date);
      }
    }
  };

  // Values for the calendar (from parent - only valid dates)
  const calendarStartDate = parseISODate(value.startDate);
  const calendarEndDate = parseISODate(value.endDate);
  const calendarRangeValue: DatesRangeValue = [calendarStartDate, calendarEndDate];
  const calendarSingleValue =
    mode === FILTER_MODE.AFTER ? calendarStartDate : mode === FILTER_MODE.BEFORE ? calendarEndDate : null;

  // Values for the inputs (local state - can have incomplete dates)
  const inputSingleValue =
    mode === FILTER_MODE.AFTER ? inputStartDate : mode === FILTER_MODE.BEFORE ? inputEndDate : null;

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.dateInputContainer)}>
        {mode === FILTER_MODE.BETWEEN ? (
          <>
            <DateInput
              placeholder="Start date"
              value={inputStartDate}
              onChange={handleStartInputChange}
              valueFormat={DISPLAY_DATE_FORMAT}
              size="lg"
              error={Boolean(dateOrderError)}
              popoverProps={{withinPortal: true, opened: false}}
              styles={{
                input: {
                  borderWidth: '1px',
                  '&[data-error]': {
                    borderColor: Color.RED_500,
                  },
                },
              }}
              aria-label="Start date"
            />
            <span className={css(styles.dateSeparator)}>-</span>
            <DateInput
              placeholder="End date"
              value={inputEndDate}
              onChange={handleEndInputChange}
              valueFormat={DISPLAY_DATE_FORMAT}
              size="lg"
              error={Boolean(dateOrderError)}
              popoverProps={{withinPortal: true, opened: false}}
              styles={{
                input: {
                  borderWidth: '1px',
                  '&[data-error]': {
                    borderColor: Color.RED_500,
                  },
                },
              }}
              aria-label="End date"
            />
          </>
        ) : (
          <DateInput
            placeholder="Enter date"
            value={inputSingleValue}
            onChange={handleSingleInputChange}
            valueFormat={DISPLAY_DATE_FORMAT}
            size="lg"
            styles={{input: {borderWidth: '1px'}}}
            popoverProps={{withinPortal: true, opened: false}}
            aria-label={mode === FILTER_MODE.AFTER ? 'After date' : 'Before date'}
          />
        )}
      </div>
      {dateOrderError && <span className={css(styles.errorText)}>{dateOrderError}</span>}
      <div className={css(styles.datePickerContainer)}>
        <div className={css(styles.modeContainer)}>
          {MODE_OPTIONS.map((option) => (
            <MantineButton
              key={option.value}
              variant="ghost"
              onClick={() => {
                handleModeChange(option.value);
              }}
              aria-pressed={mode === option.value}
            >
              {option.label}
            </MantineButton>
          ))}
          <MantineButton
            variant="ghost"
            disabled={value.startDate == null && value.endDate == null}
            onClick={() => {
              onChange({startDate: null, endDate: null});
            }}
            styles={{
              root: {marginTop: 'auto'},
            }}
          >
            Reset all
          </MantineButton>
        </div>
        <div className={css(styles.calendarContainer)}>
          {mode === FILTER_MODE.BETWEEN ? (
            <DatePicker
              key="range"
              type="range"
              value={calendarRangeValue}
              onChange={handleRangeChange as (value: DatesRangeValue) => void}
              date={displayedDate}
              onDateChange={setDisplayedDate}
              allowSingleDateInRange={true}
              size="xl"
            />
          ) : (
            <DatePicker
              key={mode}
              type="default"
              value={calendarSingleValue}
              onChange={handleSingleDateChange as (value: Date | null) => void}
              date={displayedDate}
              onDateChange={setDisplayedDate}
              size="xl"
            />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  dateInputContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    alignItems: 'center',
    marginBottom: '16px',
  },
  datePickerContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modeContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  calendarContainer: {
    display: 'flex',
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${Color.GRAY_300}`,
  },
  dateSeparator: {
    color: Color.GRAY_400,
    fontSize: '13px',
  },
  errorText: {
    color: Color.RED_500,
    ...FONT_PROXIMA_14,
    marginTop: '-12px',
    marginBottom: '12px',
  },
});
